// src/components/StreamRumble.js
// Cash Rumbles on a personal stream or a squad stream (evergreen 3.3).
//
// Mounted at SCREEN level by StreamWatchScreen / TeamStreamScreen (never
// inside StreamChat: chat unmounts when collapsed, and a checkout mounted in
// it would die with it). It renders three things:
//  - the Rumble meter: live from the streamChat/{sessionId} parent doc
//    (rumbleTotalCents / rumbleCount / rumbleLast, server-written after each
//    recorded Rumble). Fans see a wave level and a count, never an amount.
//  - the picker modal (open/onClose are the parent's): presets and limits
//    from getFloodPaymentSetup, a custom field, one-tap on a saved card, else
//    the embedded Stripe checkout (which saves the card). Amounts appear only
//    here, in the payer's own picker. Once open it holds the target it opened
//    with until closed; the server refuses a stale send.
//  - the screen-level burst watcher: claimRumbleMilestone keyed
//    `stream:${sessionId}`; the first observation per session seeds silently.
//
// Purchase limits (STEP3_5A_TIERS_DESIGN 3.7, 5.14): the warnings render only
// from 'limits'-mode data (setup.limit, a tips-limit refusal's details.limit,
// the card-limit / card-blocked tokens, cardCheck: true on a checkout). In
// 'cap' mode the server sends none of it and the picker is unchanged.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  Modal,
  ScrollView,
  ActivityIndicator,
  Image,
  Animated,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import { claimRumbleMilestone, RumbleBurst } from './TsunamiMeter';
import SpendingLimitNotice from './SpendingLimitNotice';
import {
  RUMBLE_WAVES,
  RUMBLE_MIN_CENTS,
  RUMBLE_MAX_CENTS,
  rumbleWave,
  waveLabel,
  waveColor,
  getRumbleSetup,
  sendRumbleOneTap,
  createRumbleCheckout,
  mountEmbeddedCheckout,
  rumbleErrorKind,
  rumbleErrorMessage,
  pollTipPurchaseOutcome,
} from '../utils/rumblePayment';
import {
  limitLines,
  cardLine,
  captureOutcomeLine,
  cardStatusFromError,
  limitFromError,
  savedCardStatus,
  CONFIRMING_TEXT,
  STILL_CONFIRMING_TEXT,
} from '../utils/spendingLimit';

const IS_WEB = Platform.OS === 'web';

const COLORS = {
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  DIM: '#888',
};

// Gold King Crit die: the Rumble brand mark (same asset TsunamiMeter uses).
const DIE_ICON = require('../../assets/20_dice.webp');

// Until setup answers (the server maps its own presets to the minimum).
const FALLBACK_PRESETS = [100, 500, 1000];
// After an unknown one-tap outcome the card may have been charged: refuse a
// second send from this screen for a minute (the webhook backstop records it).
const PENDING_LOCK_MS = 60000;
const PENDING_TEXT = "We couldn't confirm your Rumble yet. Please wait a minute before sending another.";
const LIMIT_TEXT = "You've reached today's limit for Super Crits and Rumbles.";
const FAILED_TEXT = 'Could not send the Rumble. Please try again.';
const CLOSE_AFTER_SENT_MS = 1600;

// Meter levels, lowest first (RUMBLE_WAVES is highest first).
const LEVELS = RUMBLE_WAVES.slice().reverse();

const EMPTY_METER = { total: 0, count: 0, last: null };

const fmtDollars = (centsAmt) => {
  const d = (Number(centsAmt) || 0) / 100;
  return `$${d % 1 ? d.toFixed(2) : d.toFixed(0)}`;
};

// '5', '5.5', '$12.50' -> cents; anything else -> null.
const parseDollars = (text) => {
  const t = String(text || '').trim().replace(/^\$/, '');
  if (!/^\d{1,6}(\.\d{0,2})?$/.test(t)) return null;
  const c = Math.round(parseFloat(t) * 100);
  return Number.isFinite(c) ? c : null;
};

function pickerLimits(setup) {
  const min = Number(setup?.minCents);
  const max = Number(setup?.maxCents);
  return {
    min: Number.isFinite(min) && min > 0 ? Math.round(min) : RUMBLE_MIN_CENTS,
    max: Number.isFinite(max) ? Math.round(max) : RUMBLE_MAX_CENTS,
  };
}

function pickerPresets(setup, min, max) {
  const raw = Array.isArray(setup?.presetsCents) && setup.presetsCents.length
    ? setup.presetsCents
    : FALLBACK_PRESETS.map((p) => Math.max(p, min));
  const out = [];
  raw.forEach((p) => {
    const n = Math.round(Number(p));
    if (Number.isInteger(n) && n >= min && n <= max && !out.includes(n)) out.push(n);
  });
  return out;
}

const snapshotTarget = (target) => (
  target && target.targetType && target.targetId
    ? { targetType: String(target.targetType), targetId: String(target.targetId) }
    : null
);

// ============================================
// RUMBLE METER (money-free: wave level + count)
// ============================================
const RumbleMeterBar = ({ meter, isTeam, pulseAnim }) => {
  const level = rumbleWave(meter.total);
  const lit = meter.count > 0 ? LEVELS.findIndex((w) => w.id === level) + 1 : 0;
  const color = waveColor(level);
  const countLabel = meter.count === 1 ? '1 Rumble' : `${meter.count.toLocaleString('en-US')} Rumbles`;
  const lastName = (meter.last && typeof meter.last.user === 'string' && meter.last.user.trim()) || 'A viewer';

  return (
    <Animated.View style={[styles.meter, { transform: [{ scale: pulseAnim }] }]}>
      <View style={styles.meterRow}>
        <Image source={DIE_ICON} style={styles.meterDie} resizeMode="contain" />
        <Text style={styles.meterTitle}>RUMBLE METER</Text>
        {meter.count > 0 ? (
          <Text style={[styles.meterLevel, { color }]}>{waveLabel(level).toUpperCase()}</Text>
        ) : null}
        <View style={{ flex: 1 }} />
        {meter.count > 0 ? <Text style={styles.meterCount}>{countLabel}</Text> : null}
      </View>
      <View style={styles.meterSegs}>
        {LEVELS.map((w, i) => (
          <View key={w.id} style={[styles.meterSeg, i < lit && { backgroundColor: color }]} />
        ))}
      </View>
      <Text style={styles.meterLast} numberOfLines={1}>
        {meter.last
          ? `${lastName} sent a ${waveLabel(meter.last.wave)} Rumble!`
          : `No Rumbles yet — be the first to shake the ${isTeam ? 'squad' : 'stream'}.`}
      </Text>
    </Animated.View>
  );
};

// ============================================
// STREAM RUMBLE (meter + picker modal + burst watcher)
// ============================================
export default function StreamRumble({
  target,
  sessionId,
  rumblesEnabled,
  open,
  onClose,
  onRequireAuth,
}) {
  const isTeam = target?.targetType === 'team';

  // Parent callbacks live in refs: a re-render of the screen (polls, chat,
  // heartbeats) must never tear down the embedded checkout mid-payment.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onRequireAuthRef = useRef(onRequireAuth);
  onRequireAuthRef.current = onRequireAuth;

  // Auth (the meter is public; the picker needs a signed-in payer)
  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);
  const [authReady, setAuthReady] = useState(() => !!auth.currentUser);
  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUid(u ? u.uid : null);
    setAuthReady(true);
  }), []);

  // ------------------------------------------------------------------
  // Meter + burst watcher
  // ------------------------------------------------------------------
  const [meter, setMeter] = useState(EMPTY_METER);
  const [burst, setBurst] = useState(0);
  const prevMeterRef = useRef({ sessionId: null, total: null, fromCache: false });
  const pulseAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    prevMeterRef.current = { sessionId, total: null, fromCache: false };
    setMeter(EMPTY_METER);
    if (!sessionId) return undefined;
    const unsub = onSnapshot(
      doc(db, 'streamChat', sessionId),
      (snap) => {
        const d = snap.exists() ? (snap.data() || {}) : {};
        const total = Math.max(0, Number(d.rumbleTotalCents) || 0);
        const count = Math.max(0, Math.floor(Number(d.rumbleCount) || 0));
        const last = d.rumbleLast && typeof d.rumbleLast === 'object' ? d.rumbleLast : null;
        const fromCache = !!snap.metadata?.fromCache;
        const prev = prevMeterRef.current;
        // Seed silently on the first observation (and again when a cached
        // first read is replaced by the server's): a viewer arriving after a
        // crossing gets no stale burst. After that every change goes through
        // the shared claim; a drop re-arms it.
        if (prev.total !== null && !(prev.fromCache && !fromCache) && total !== prev.total) {
          const claimed = claimRumbleMilestone(`stream:${sessionId}`, prev.total, total);
          if (claimed) setBurst(claimed);
          if (total > prev.total) {
            Animated.sequence([
              Animated.timing(pulseAnim, { toValue: 1.04, duration: 150, useNativeDriver: !IS_WEB }),
              Animated.timing(pulseAnim, { toValue: 1, duration: 150, useNativeDriver: !IS_WEB }),
            ]).start();
          }
        }
        prevMeterRef.current = { sessionId, total, fromCache };
        setMeter({ total, count, last });
      },
      (err) => {
        console.warn('[StreamRumble] meter listener failed:', err?.code || err?.message);
      },
    );
    return () => unsub();
  }, [sessionId, pulseAnim]);

  const clearBurst = useCallback(() => setBurst(0), []);

  // ------------------------------------------------------------------
  // Picker modal
  // ------------------------------------------------------------------
  const [activeType, setActiveType] = useState(null);
  const [setup, setSetup] = useState(null);
  const [setupState, setSetupState] = useState('idle'); // idle | loading | ready | error
  const [preset, setPreset] = useState(null);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [embedded, setEmbedded] = useState(null); // { clientSecret, publishableKey, cardCheck, purchaseId } | null
  // Purchase limits ('limits' mode only; all stay null in 'cap' mode):
  const [limitErr, setLimitErr] = useState(null);       // { limit, amountCents } from a tips-limit refusal
  const [cardRefusal, setCardRefusal] = useState(null); // 'card_limit' | 'card_blocked' from a one-tap refusal
  const [confirm, setConfirm] = useState(null);         // 'confirming' | 'pending' after a held checkout
  const [outcomeLine, setOutcomeLine] = useState(null); // L7 / L8 / L8b

  const activeTargetRef = useRef(null);
  const openSeqRef = useRef(0);       // bumps on every open/close: late replies are dropped
  const busyRef = useRef(false);      // survives a close, so a reopen can't double-send
  const pendingUntilRef = useRef(0);
  const closeTimerRef = useRef(null);
  const checkoutBoxRef = useRef(null);

  const requestClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    if (onCloseRef.current) onCloseRef.current();
  }, []);

  const scheduleClose = () => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(requestClose, CLOSE_AFTER_SENT_MS);
  };

  useEffect(() => () => {
    openSeqRef.current += 1;
    clearTimeout(closeTimerRef.current);
  }, []);

  // Open: snapshot the target and load setup. Keyed on open + payer only, so
  // a parent re-render with a new target object never refetches mid-flow.
  useEffect(() => {
    openSeqRef.current += 1;
    clearTimeout(closeTimerRef.current);
    setEmbedded(null);
    setSent(false);
    setError(null);
    setCustom('');
    setLimitErr(null);
    setCardRefusal(null);
    setConfirm(null);
    setOutcomeLine(null);
    if (!open) return;

    const t = snapshotTarget(target);
    activeTargetRef.current = t;
    setActiveType(t ? t.targetType : null);
    setSetup(null);
    if (Date.now() < pendingUntilRef.current) setError(PENDING_TEXT);
    if (!IS_WEB || !uid) { setSetupState('idle'); return; }
    if (!t) { setSetupState('error'); return; }

    const seq = openSeqRef.current;
    setSetupState('loading');
    getRumbleSetup(t)
      .then((data) => {
        if (openSeqRef.current !== seq) return;
        const lim = pickerLimits(data);
        const presets = pickerPresets(data, lim.min, lim.max);
        setSetup(data || null);
        setPreset(presets.length > 1 ? presets[1] : (presets[0] ?? null));
        setSetupState('ready');
      })
      .catch((err) => {
        if (openSeqRef.current !== seq) return;
        console.warn('[StreamRumble] setup failed:', err?.code || err?.message);
        setSetupState('error');
      });
  }, [open, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-read setup without resetting the picker ('limits' mode: a cooldown
  // ended, so the room and the picker maximum moved).
  const refreshSetup = () => {
    const t = activeTargetRef.current;
    if (!IS_WEB || !uid || !t) return;
    const seq = openSeqRef.current;
    getRumbleSetup(t)
      .then((data) => {
        if (openSeqRef.current !== seq) return;
        setSetup(data || null);
        setLimitErr(null);
      })
      .catch((err) => {
        console.warn('[StreamRumble] setup refresh failed:', err?.code || err?.message);
      });
  };

  // Embedded checkout: keyed on the clientSecret only; callbacks via refs.
  const onCheckoutCompleteRef = useRef(null);
  onCheckoutCompleteRef.current = () => {
    const held = embedded && embedded.cardCheck === true && embedded.purchaseId ? embedded.purchaseId : null;
    setEmbedded(null);
    if (!held) {
      // Paid: the webhook records it and the meter moves on its own.
      setSent(true);
      scheduleClose();
      return;
    }
    // Card checks on: the payment is only authorized until the server checks
    // the card (3.7), so confirm it before saying sent.
    const seq = openSeqRef.current;
    setConfirm('confirming');
    pollTipPurchaseOutcome('rumble', held, { isAlive: () => openSeqRef.current === seq })
      .then(({ status, reason }) => {
        if (openSeqRef.current !== seq) return;
        if (status === 'completed') { setConfirm(null); setSent(true); scheduleClose(); return; }
        if (status === 'failed') {
          setConfirm(null);
          const line = captureOutcomeLine(reason, { verify: setup?.limit?.verify });
          if (line) setOutcomeLine(line); else setError(FAILED_TEXT);
          return;
        }
        setConfirm('pending');
      });
  };
  const onCheckoutErrorRef = useRef(null);
  onCheckoutErrorRef.current = () => {
    setEmbedded(null);
    setError('Could not open the payment form. Please try again.');
  };
  useEffect(() => {
    if (!embedded?.clientSecret) return undefined;
    return mountEmbeddedCheckout(checkoutBoxRef, embedded, onCheckoutCompleteRef, onCheckoutErrorRef);
  }, [embedded]);

  const limits = pickerLimits(setup);
  const presets = pickerPresets(setup, limits.min, limits.max);
  const customText = custom.trim();
  const amountCents = customText ? parseDollars(customText) : preset;
  const amountValid = Number.isInteger(amountCents) && amountCents >= limits.min && amountCents <= limits.max;
  const customInvalid = !!customText && !amountValid;

  // 'limits' mode only ('cap' sends no limit object): the account's room, and
  // whether the saved card can be used here.
  const limit = setup?.limit && typeof setup.limit === 'object' ? setup.limit : null;
  const savedStatus = savedCardStatus(limit);
  const limitVerify = limit?.verify;
  // A custom amount above the room shows L2 instead of the range sentence.
  const customCents = customText ? parseDollars(customText) : null;
  const overRoom = !!limit && limit.limited !== false && Number.isInteger(customCents)
    && customCents >= limits.min && customCents > (Number(limit.remainingCents) || 0);
  const clearLimitNotices = () => { setLimitErr(null); setOutcomeLine(null); };

  // opts.skipOneTap: "Use another card" goes straight to the embedded
  // checkout, as does a saved card the setup says can't be used here.
  // onPress / onSubmitEditing pass an event object, which carries no flag.
  const send = async (opts) => {
    if (busyRef.current) return;
    const t = activeTargetRef.current;
    if (!t || !amountValid) return;
    if (Date.now() < pendingUntilRef.current) { setError(PENDING_TEXT); return; }

    const skipOneTap = !!(opts && opts.skipOneTap === true) || savedStatus !== 'ok';
    const seq = openSeqRef.current;
    const stillOpen = () => openSeqRef.current === seq;
    const amt = amountCents;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    clearLimitNotices();
    let stage = 'onetap';
    try {
      if (setup?.hasSavedCard && !skipOneTap) {
        try {
          await sendRumbleOneTap(t, amt);
          if (stillOpen()) { setSent(true); scheduleClose(); }
          return;
        } catch (err) {
          // Only a saved card that needs SCA / none on file falls through to
          // checkout. Never after tips-pending (the card may be charged),
          // tips-unavailable or tips-limit (checkout is refused the same way).
          // A card refusal ('card') waits for "Use another card".
          if (rumbleErrorKind(err) !== 'checkout') throw err;
        }
      }
      if (!stillOpen()) return;
      stage = 'checkout';
      const data = await createRumbleCheckout(t, amt);
      if (!stillOpen()) return;
      setCardRefusal(null);
      setEmbedded({
        clientSecret: data.clientSecret,
        publishableKey: data.publishableKey || setup?.publishableKey || null,
        cardCheck: data.cardCheck === true,
        purchaseId: typeof data.purchaseId === 'string' ? data.purchaseId : null,
      });
    } catch (err) {
      const kind = rumbleErrorKind(err);
      if (kind === 'pending' && stage === 'onetap') {
        pendingUntilRef.current = Date.now() + PENDING_LOCK_MS;
      }
      if (stillOpen()) {
        const lim = kind === 'limit' ? limitFromError(err) : null;
        const cardStatus = kind === 'card' ? cardStatusFromError(err) : null;
        if (lim) setLimitErr({ limit: lim, amountCents: amt });
        else if (cardStatus) setCardRefusal(cardStatus);
        else {
          // A transport failure while starting checkout charges nothing.
          setError(kind === 'pending' && stage === 'checkout'
            ? 'Could not start checkout. Please try again.'
            : rumbleErrorMessage(err));
        }
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const goSignUp = () => {
    requestClose();
    if (onRequireAuthRef.current) onRequireAuthRef.current();
  };

  const blockedReason = () => {
    if (setupState === 'error' || !setup) return "Rumbles aren't available right now. Please try again in a moment.";
    if (!setup.enabled || typeof setup.publishableKey !== 'string' || !setup.publishableKey) {
      return "Rumbles aren't available just yet — check back soon.";
    }
    if (setup.tipsEnabled === false) {
      // 'limits' mode: the numbered at-limit line (L3, L3b, L3p, L4) instead.
      if (setup.reason === 'tips-limit') return limit && limitLines(limit)?.atLimit ? 'limit' : LIMIT_TEXT;
      return (typeof setup.reason === 'string' && setup.reason.trim()) || "Rumbles aren't open here right now.";
    }
    // Setup already says tips-limit when today's room is under the minimum;
    // this only catches an older reply that omitted it.
    if (limits.max < limits.min) return limit && limitLines(limit)?.atLimit ? 'limit' : LIMIT_TEXT;
    return null;
  };

  const modalIsTeam = (activeType || target?.targetType) === 'team';
  // No member count on squads: the count the server shows (verified, not an
  // admin) can include a member the split leaves out (a held or restricted
  // account), and showing the real split size would reveal that account state.
  const subtitle = modalIsTeam
    ? 'Shake the squad — your Rumble lights up the chat and is shared by the squad members streaming now.'
    : 'Shake the stream — your Rumble lights up the chat.';

  const cancelButton = (label = 'Cancel') => (
    <TouchableOpacity style={styles.cancelBtn} onPress={requestClose}>
      <Text style={styles.cancelBtnText}>{label}</Text>
    </TouchableOpacity>
  );

  const notice = (message) => (
    <>
      <Text style={styles.title}>Send a Rumble</Text>
      <Text style={styles.sub}>{message}</Text>
      {cancelButton('Close')}
    </>
  );

  const renderBody = () => {
    if (sent) {
      return (
        <View style={styles.sentBox}>
          <Ionicons name="flash" size={34} color={COLORS.GOLD} />
          <Text style={styles.sentText}>Rumble sent! ⚡</Text>
        </View>
      );
    }
    if (confirm === 'confirming') {
      return (
        <>
          <Text style={styles.title}>Complete your Rumble</Text>
          <ActivityIndicator style={styles.loading} color={COLORS.GOLD} />
          <Text style={styles.hint}>{CONFIRMING_TEXT}</Text>
        </>
      );
    }
    if (confirm === 'pending') return notice(STILL_CONFIRMING_TEXT);
    if (!IS_WEB) return notice('Rumbles are available on the web app.');
    if (!authReady) return <ActivityIndicator style={styles.loading} color={COLORS.GOLD} />;
    if (!uid) {
      return (
        <>
          <Text style={styles.title}>Send a Rumble</Text>
          <Text style={styles.sub}>Sign up or log in to send a Rumble.</Text>
          <TouchableOpacity style={styles.sendBtn} onPress={goSignUp}>
            <Text style={styles.sendBtnText}>Sign up</Text>
          </TouchableOpacity>
          {cancelButton()}
        </>
      );
    }
    if (embedded) {
      return (
        <>
          <Text style={styles.title}>Complete your Rumble</Text>
          {/* RN Web: the View ref is the DOM node Stripe mounts its iframe into. */}
          <View ref={checkoutBoxRef} style={styles.checkoutBox} />
          {cancelButton()}
        </>
      );
    }
    if (setupState === 'loading' || setupState === 'idle') {
      return <ActivityIndicator style={styles.loading} color={COLORS.GOLD} />;
    }
    const blocked = blockedReason();
    if (blocked === 'limit') {
      return (
        <>
          <Text style={styles.title}>Send a Rumble</Text>
          <SpendingLimitNotice limit={limit} onExpire={refreshSetup} onBeforeNavigate={requestClose} />
          {cancelButton('Close')}
        </>
      );
    }
    if (blocked) return notice(blocked);

    const wave = amountValid ? rumbleWave(amountCents) : null;
    return (
      <>
        <Text style={styles.title}>Send a Rumble</Text>
        <Text style={styles.sub}>{subtitle}</Text>

        <View style={styles.chips}>
          {presets.map((c) => {
            const active = !customText && preset === c;
            return (
              <TouchableOpacity
                key={c}
                style={[styles.chip, active && styles.chipActive]}
                onPress={() => { setPreset(c); setCustom(''); setError(null); clearLimitNotices(); }}
                disabled={busy}
              >
                <Text style={[styles.chipText, active && styles.chipTextActive]}>{fmtDollars(c)}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <TextInput
          style={[styles.customInput, customInvalid && styles.customInputBad]}
          placeholder={`Custom amount (${fmtDollars(limits.min)}–${fmtDollars(limits.max)})`}
          placeholderTextColor="#666"
          keyboardType="decimal-pad"
          value={custom}
          onChangeText={(v) => { setCustom(v); setError(null); clearLimitNotices(); }}
          onSubmitEditing={send}
          editable={!busy}
          maxLength={10}
        />

        <Text style={styles.hint}>
          {customInvalid && overRoom
            ? ''
            : customInvalid
            ? `Enter an amount from ${fmtDollars(limits.min)} to ${fmtDollars(limits.max)}.`
            : wave
              ? (
                <>
                  <Text style={[styles.hintWave, { color: waveColor(wave) }]}>{waveLabel(wave)} Rumble</Text>
                  {' — bigger Rumbles make bigger waves in chat'}
                </>
              )
              : ''}
        </Text>

        {/* 'limits' mode: the room under the amounts (L1), or L2 for an
            amount above it; a refusal's own numbers replace it. */}
        {limit && !limitErr ? (
          <SpendingLimitNotice
            limit={limit}
            amountCents={overRoom ? customCents : null}
            onExpire={refreshSetup}
            onBeforeNavigate={requestClose}
          />
        ) : null}
        {limitErr ? (
          <SpendingLimitNotice
            limit={limitErr.limit}
            amountCents={limitErr.amountCents}
            onExpire={refreshSetup}
            onBeforeNavigate={requestClose}
          />
        ) : null}

        {setup?.hasSavedCard && setup?.card && savedStatus === 'ok' ? (
          <Text style={styles.hint}>
            One tap on your saved {String(setup.card.brand || 'card').toUpperCase()} ····{setup.card.last4}
          </Text>
        ) : null}
        {setup?.hasSavedCard && savedStatus !== 'ok' && !cardRefusal ? (
          <SpendingLimitNotice
            line={cardLine(savedStatus, { saved: true, verify: limitVerify })}
            tone="error"
            onUseAnotherCard={amountValid ? () => send({ skipOneTap: true }) : undefined}
            onBeforeNavigate={requestClose}
            disabled={busy}
          />
        ) : null}
        {cardRefusal ? (
          <SpendingLimitNotice
            line={cardLine(cardRefusal, { verify: limitVerify })}
            tone="error"
            onUseAnotherCard={amountValid ? () => send({ skipOneTap: true }) : undefined}
            onBeforeNavigate={requestClose}
            disabled={busy}
          />
        ) : null}
        {outcomeLine ? (
          <SpendingLimitNotice line={outcomeLine} tone="error" onBeforeNavigate={requestClose} />
        ) : null}

        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        <TouchableOpacity
          style={[styles.sendBtn, (busy || !amountValid) && { opacity: 0.6 }]}
          onPress={send}
          disabled={busy || !amountValid}
        >
          {busy ? (
            <ActivityIndicator size="small" color="#0b0b0b" />
          ) : (
            <Text style={styles.sendBtnText}>
              {amountValid ? `Send ${fmtDollars(amountCents)} Rumble` : 'Send Rumble'}
            </Text>
          )}
        </TouchableOpacity>
        {cancelButton()}
      </>
    );
  };

  const showMeter = !!sessionId && (rumblesEnabled === true || meter.count > 0);

  return (
    <>
      {showMeter ? <RumbleMeterBar meter={meter} isTeam={isTeam} pulseAnim={pulseAnim} /> : null}

      <Modal visible={!!open} transparent animationType="fade" onRequestClose={requestClose}>
        {/* The backdrop doesn't close over a live checkout or an in-flight
            send; Cancel always does. */}
        <Pressable style={styles.backdrop} onPress={embedded || busy || confirm === 'confirming' ? undefined : requestClose}>
          <Pressable style={[styles.card, embedded && styles.cardWide]} onPress={(e) => e.stopPropagation()}>
            <ScrollView
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              {renderBody()}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Screen-level burst: portals to body (or the fullscreen element). */}
      <RumbleBurst
        visible={!!burst}
        milestone={burst}
        onDone={clearBurst}
        title="RUMBLE!"
        subtitle={isTeam ? 'The crowd is shaking the squad!' : 'The crowd is shaking the stream!'}
      />
    </>
  );
}

// ============================================
// STYLES (dark card + gold, matching the StreamWatch modals)
// ============================================
const styles = StyleSheet.create({
  // Meter
  meter: {
    backgroundColor: COLORS.CARD, borderColor: COLORS.CARD_BORDER, borderWidth: 1,
    borderRadius: 10, paddingVertical: 8, paddingHorizontal: 12, marginVertical: 6,
  },
  meterRow: { flexDirection: 'row', alignItems: 'center' },
  meterDie: { width: 16, height: 16, marginRight: 6 },
  meterTitle: { color: COLORS.GOLD, fontSize: 11, fontWeight: '800', letterSpacing: 0.8 },
  meterLevel: { fontSize: 11, fontWeight: '800', letterSpacing: 0.6, marginLeft: 8 },
  meterCount: { color: '#999', fontSize: 11, fontWeight: '700' },
  meterSegs: { flexDirection: 'row', gap: 4, marginTop: 7 },
  meterSeg: { flex: 1, height: 5, borderRadius: 3, backgroundColor: '#262626' },
  meterLast: { color: '#bbb', fontSize: 11.5, marginTop: 6 },

  // Modal
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  card: {
    width: '100%', maxWidth: 380, maxHeight: '92%', backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER, borderWidth: 1, borderRadius: 14, padding: 20,
  },
  cardWide: { maxWidth: 460 },
  title: { color: '#fff', fontSize: 17, fontWeight: '800' },
  sub: { color: '#999', fontSize: 12.5, lineHeight: 18, marginTop: 6 },
  loading: { paddingVertical: 28 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16 },
  chip: {
    flexGrow: 1, flexBasis: 0, minWidth: 64, alignItems: 'center', backgroundColor: '#161616',
    borderColor: '#2a2a2a', borderWidth: 1, borderRadius: 8, paddingVertical: 10,
  },
  chipActive: { backgroundColor: '#241d00', borderColor: COLORS.GOLD },
  chipText: { color: '#bbb', fontSize: 14, fontWeight: '700' },
  chipTextActive: { color: COLORS.GOLD },
  customInput: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, color: COLORS.TEXT, fontSize: 13,
    paddingVertical: 9, paddingHorizontal: 12, marginTop: 12,
  },
  customInputBad: { borderColor: '#e05555' },
  hint: { color: '#777', fontSize: 12, marginTop: 10, textAlign: 'center' },
  hintWave: { fontWeight: '800' },
  errorText: { color: '#e05555', fontSize: 12, marginTop: 10, textAlign: 'center' },
  sendBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12,
    alignItems: 'center', marginTop: 14,
  },
  sendBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
  cancelBtn: { alignItems: 'center', marginTop: 10, paddingVertical: 8 },
  cancelBtnText: { color: COLORS.DIM, fontSize: 13, fontWeight: '600' },
  checkoutBox: { minHeight: 420, marginTop: 14, borderRadius: 10, overflow: 'hidden', backgroundColor: '#fff' },
  sentBox: { alignItems: 'center', paddingVertical: 18 },
  sentText: { color: COLORS.GOLD, fontSize: 16, fontWeight: '800', marginTop: 10 },
});
